import { atom } from "jotai";
import { canvasIdAtom } from "./canvasAtom";
import { getCookie } from "../utils";

async function fetchCanvasFolderId(canvasId: string): Promise<string | null> {
  try {
    const res = await fetch(
      `/api/org/v2/folders/lookup?asset_ids=canvas:${encodeURIComponent(canvasId)}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-csrftoken": getCookie("csrftoken") || "",
        },
      },
    );

    if (!res.ok) {
      console.error(`Failed to look up canvas folder (${res.status})`);
      return null;
    }

    const data = await res.json();
    const folder = data?.results?.[0]?.folder;
    return folder?.id ?? null;
  } catch (error) {
    console.error("Error fetching canvas folder:", error);
    return null;
  }
}

/**
 * Fire-and-forget: moves an asset into an org-service folder.
 * Logs errors but never throws.
 */
export async function moveAssetToFolder(
  folderId: string,
  assetType: string,
  assetId: string,
): Promise<void> {
  try {
    const res = await fetch(
      `/api/org/v2/folders/${encodeURIComponent(folderId)}/assets`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-csrftoken": getCookie("csrftoken") || "",
        },
        body: JSON.stringify({
          asset_ids: [`${assetType}:${assetId}`],
        }),
      },
    );

    if (!res.ok) {
      console.error(
        `Failed to move ${assetType}:${assetId} to folder ${folderId} (${res.status})`,
      );
    }
  } catch (error) {
    console.error(`Error moving ${assetType}:${assetId} to folder:`, error);
  }
}

// undefined = not yet fetched, null = canvas is not in any folder, string = folder ID
const baseFolderIdAtom = atom<string | null | undefined>(undefined);
baseFolderIdAtom.debugLabel = "baseFolderIdAtom";

// Tracks which canvas ID was last fetched so we re-fetch when navigating between canvases
const folderFetchedForCanvasIdAtom = atom<string | null>(null);
folderFetchedForCanvasIdAtom.debugLabel = "folderFetchedForCanvasIdAtom";

/**
 * Holds the org-service folder ID for the current canvas.
 * undefined = lookup hasn't completed yet, null = canvas is not in a folder.
 * Re-fetches automatically when the canvas ID changes (e.g. client-side navigation).
 */
export const canvasFolderIdAtom = atom(
  (get) => get(baseFolderIdAtom),
  async (get, set) => {
    const canvasId = get(canvasIdAtom);
    const lastFetchedCanvasId = get(folderFetchedForCanvasIdAtom);

    if (!canvasId || canvasId === lastFetchedCanvasId) {
      return;
    }

    set(folderFetchedForCanvasIdAtom, canvasId);
    set(baseFolderIdAtom, undefined);
    const folderId = await fetchCanvasFolderId(canvasId);
    set(baseFolderIdAtom, folderId);
  },
);
canvasFolderIdAtom.debugLabel = "canvasFolderIdAtom";

/**
 * Jotai setter that moves an asset into the canvas's folder.
 * Reads the folder ID from canvasFolderIdAtom; no-ops if the canvas isn't in a folder
 * or if the folder lookup hasn't completed yet.
 */
export const moveAssetToCanvasFolderSetter = atom(
  null,
  async (
    get,
    _set,
    { assetType, assetId }: { assetType: string; assetId: string },
  ) => {
    const folderId = get(baseFolderIdAtom);

    if (!folderId) {
      return;
    }

    await moveAssetToFolder(folderId, assetType, assetId);
  },
);
moveAssetToCanvasFolderSetter.debugLabel = "moveAssetToCanvasFolderSetter";
